import { MapCell } from "../models/warehouse.mapcell";

export enum ALPHABETIC {
  A,
  B,
  C,
  D,
  E,
  F,
  G,
  H,
  I,
  J,
  K,
  L,
  M,
  N,
  O,
  P,
  Q,
  R,
  S,
  T,
  U,
  V,
  W,
  X,
  Y,
  Z,
}

export const alphabeticIndex = {
  A: 0,
  B: 1,
  C: 2,
  D: 3,
  E: 4,
  F: 5,
  G: 6,
  H: 7,
  I: 8,
  J: 9,
  K: 10,
  L: 11,
  M: 12,
  N: 13,
  O: 14,
  P: 15,
  Q: 16,
  R: 17,
  S: 18,
  T: 19,
  U: 20,
  V: 21,
  W: 22,
  X: 23,
  Y: 24,
  Z: 25,
};

export enum AxisConfigType {
  NUMERIC = "numeric",
  ALPHABETICAL = "alphabetical",
}

export enum AxisConfigOrdination {
  ASC = "asc",
  DESC = "desc",
}

export enum AxisName {
  ROW = "row",
  COLUMN = "column",
  LEVEL = "level",
}

export type AxisConfig = {
  type: AxisConfigType;
  ordination: AxisConfigOrdination;
  size: number;
  initial?: number;
  prefix?: string;
  suffix?: string;
};

export type OverrideName = {
  axisName: AxisName;
  index: number;
  newName: string;
};

export type ConfigsToBuilding = {
  configColumn: AxisConfig;
  configRow: AxisConfig;
  configLevel: AxisConfig;
  overrideNames?: OverrideName[];
  mapId?: number;
  previousMapCells?: MapCell[];
};

export type GenerateName = {
  axisName: AxisName;
  axisConfig: AxisConfig;
  index: number;
  alphabeticalConfigs: AlphabeticalConfig;
};

export type SetInitialValue = {
  ordination: AxisConfigOrdination;
  initial?: number;
};

export type InitialsValue = {
  initialValue: number;
  indexRepeatWord: number;
};

export type AlphabeticalConfig = {
  row: InitialsValue;
  column: InitialsValue;
  level: InitialsValue;
};

export type MatrixLengths = {
  rowsLength: number;
  columnsLength: number;
  levelsLength: number;
};

export const buildingMapCells = ({
  configRow,
  configColumn,
  configLevel,
  overrideNames,
  mapId,
  previousMapCells = [],
}: ConfigsToBuilding): {
  mapCells: MapCell[];
  matrixValues: OverrideName[];
  matrixLengths: MatrixLengths;
} => {
  const lastIndexAlphabetic = 25;
  const firstIndexAlphabetic = 0;
  const mapCells: MapCell[] = [];
  const matrixValues: OverrideName[] = [];

  const getAlphabeticalConfig = ({ configRow, configColumn, configLevel }: ConfigsToBuilding) => {
    const setInitialValue = ({ ordination, initial }: SetInitialValue) => {
      let initialValueToReturn: number = firstIndexAlphabetic;

      if (ordination === AxisConfigOrdination.DESC) {
        initialValueToReturn = initial && ALPHABETIC[initial] ? initial : lastIndexAlphabetic;
      }

      if (ordination === AxisConfigOrdination.ASC) {
        initialValueToReturn = initial && ALPHABETIC[initial] ? initial : firstIndexAlphabetic;
      }

      return initialValueToReturn;
    };

    const initialValueRow = setInitialValue({
      ordination: configRow.ordination,
      initial: configRow.initial,
    });

    const initialValueColumn = setInitialValue({
      ordination: configColumn.ordination,
      initial: configColumn.initial,
    });

    const initialValueLevel = setInitialValue({
      ordination: configLevel.ordination,
      initial: configLevel.initial,
    });

    return {
      row: { initialValue: initialValueRow, indexRepeatWord: -1 },
      column: { initialValue: initialValueColumn, indexRepeatWord: -1 },
      level: { initialValue: initialValueLevel, indexRepeatWord: -1 },
    };
  };

  const generateName = ({ axisName, axisConfig, index, alphabeticalConfigs }: GenerateName) => {
    const { ordination, type, size, prefix, suffix, initial } = axisConfig;

    const { initialValue, indexRepeatWord } = alphabeticalConfigs[axisName];

    let name = "";

    if (ordination === AxisConfigOrdination.DESC && type === AxisConfigType.NUMERIC) {
      if (initial) {
        name = (initial - index).toString();
      } else {
        name = (size - index).toString();
      }
    }

    if (ordination === AxisConfigOrdination.ASC && type === AxisConfigType.NUMERIC) {
      if (initial) {
        name = (initial + index).toString();
      } else {
        name = (index + 1).toString();
      }
    }

    if (ordination === AxisConfigOrdination.DESC && type === AxisConfigType.ALPHABETICAL) {
      name =
        indexRepeatWord >= 0
          ? ALPHABETIC[indexRepeatWord] + ALPHABETIC[initialValue]
          : ALPHABETIC[initialValue];

      if (initialValue === firstIndexAlphabetic) {
        alphabeticalConfigs[axisName].initialValue = lastIndexAlphabetic;
        alphabeticalConfigs[axisName].indexRepeatWord += 1;
      } else {
        alphabeticalConfigs[axisName].initialValue -= 1;
      }
    }

    if (ordination === AxisConfigOrdination.ASC && type === AxisConfigType.ALPHABETICAL) {
      name =
        indexRepeatWord >= 0
          ? ALPHABETIC[indexRepeatWord] + ALPHABETIC[initialValue]
          : ALPHABETIC[initialValue];

      if (initialValue === lastIndexAlphabetic) {
        alphabeticalConfigs[axisName].initialValue = firstIndexAlphabetic;
        alphabeticalConfigs[axisName].indexRepeatWord += 1;
      } else {
        alphabeticalConfigs[axisName].initialValue += 1;
      }
    }

    return `${prefix || ""}${name}${suffix || ""}`;
  };

  const generateMapCell = ({
    indexRow,
    indexColumn,
    indexLevel,
    rowName,
    columnName,
    levelName,
    mapId,
    isActive,
  }: MapCell) => {
    if (mapId) {
      return {
        name: `${rowName}${columnName}/${levelName}`,
        indexRow,
        indexColumn,
        indexLevel,
        rowName,
        columnName,
        levelName,
        mapId,
        isActive,
        statusId: 1,
        status_id: 1,
      };
    }

    return {
      name: `${rowName}${columnName}/${levelName}`,
      indexRow,
      indexColumn,
      indexLevel,
      rowName,
      columnName,
      levelName,
      isActive,
    };
  };

  const getOverrideNames = (overrideNames?: OverrideName[]) => {
    if (!overrideNames?.length) return;

    const row = overrideNames?.filter((override) => override.axisName === AxisName.ROW);
    const column = overrideNames?.filter((override) => override.axisName === AxisName.COLUMN);
    const level = overrideNames?.filter((override) => override.axisName === AxisName.LEVEL);

    return {
      overrideRow: row,
      overrideColumn: column,
      overrideLevel: level,
    };
  };

  const searchOverrideName = (overrideNames: OverrideName[], index: number) => {
    if (!overrideNames?.length) return;

    const override = overrideNames?.filter((override) => override.index === index);

    if (override && override.length > 0) {
      return override[0].newName;
    }
  };

  (() => {
    const alphabeticalConfigsRows = getAlphabeticalConfig({ configRow, configColumn, configLevel });
    const override = getOverrideNames(overrideNames);

    const overrideRow = override?.overrideRow || [];
    const overrideColumn = override?.overrideColumn || [];
    const overrideLevel = override?.overrideLevel || [];

    for (let row = 0; row < configRow.size; row += 1) {
      const overrideRowName = searchOverrideName(overrideRow, row);
      const rowName = generateName({
        axisName: AxisName.ROW,
        index: row,
        axisConfig: configRow,
        alphabeticalConfigs: alphabeticalConfigsRows,
      });

      matrixValues.push({
        axisName: AxisName.ROW,
        index: row,
        newName: overrideRowName || rowName,
      });
      const alphabeticalConfigsColumns = getAlphabeticalConfig({
        configRow,
        configColumn,
        configLevel,
      });

      for (let column = 0; column < configColumn.size; column += 1) {
        const overrideColumnName = searchOverrideName(overrideColumn, column);
        const columnName = generateName({
          axisName: AxisName.COLUMN,
          index: column,
          axisConfig: configColumn,
          alphabeticalConfigs: alphabeticalConfigsColumns,
        });
        const alphabeticalConfigToLevel = getAlphabeticalConfig({
          configRow,
          configColumn,
          configLevel,
        });

        matrixValues.push({
          axisName: AxisName.COLUMN,
          index: column,
          newName: overrideColumnName || columnName,
        });

        for (let level = 0; level < configLevel.size; level += 1) {
          const overrideLevelName = searchOverrideName(overrideLevel, level);
          const levelName = generateName({
            axisName: AxisName.LEVEL,
            index: level,
            axisConfig: configLevel,
            alphabeticalConfigs: alphabeticalConfigToLevel,
          });
          const currentRowName = overrideRowName || rowName;
          const currentColumnName = overrideColumnName || columnName;
          const currentLevelName = overrideLevelName || levelName;

          const previousCells = previousMapCells.find((previousMapCell) => {
            return !!(
              previousMapCell.indexColumn === column &&
              previousMapCell.indexRow === row &&
              previousMapCell.indexLevel === level
            );
          });

          const mapCell = generateMapCell({
            indexRow: row,
            indexColumn: column,
            indexLevel: level,
            rowName: currentRowName,
            columnName: currentColumnName,
            levelName: currentLevelName,
            mapId,
            isActive: previousCells?.isActive ?? true,
          });

          mapCells.push(mapCell);
        }
      }
    }
  })();

  return {
    mapCells,
    matrixValues,
    matrixLengths: {
      rowsLength: configRow.size,
      columnsLength: configColumn.size,
      levelsLength: configLevel.size,
    },
  };
};
